import React, {useEffect, useMemo} from 'react';
import {useSearchParams} from 'react-router';
import {
    formatVersionNumber,
    parseVersionFromSnapshotPath,
    useReportDocumentSubscription,
    useReportVersionDocSubscription,
    useReportVersionsSubscription
} from './useReportDocumentSubscription';
import {apiMetaToHeaderData, mergeHeaderData, readSerializableHeaderFromYDoc} from '../../../utils/documentHeaderMeta';
import useYDocStore from '../../../stores/useYDocStore';
import {
    hasActivePreviewDraft,
    resolvePreviewVersion,
    shouldUsePreviewDraft,
} from '../../../utils/previewDraft';
import {useReportStore} from '../useReportStore';
import usePreviewAuthors from './usePreviewAuthors';
import {useVersionBlob} from './useVersionBlob';

/**
 * Custom hook to manage version selection and loading
 * Handles version URL params, document subscription, and blob loading
 *
 * @param {string} reportId - The report document ID
 * @param {boolean} useLocalState - If true, manage version in local state instead of URL params (for embedded previews)
 */
const useVersionManagement = (reportId, useLocalState = false) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [localVersion, setLocalVersion] = React.useState(null);

    // Use local state for embedded views, URL params for standalone views
    const version = useLocalState ? localVersion : searchParams.get('version');
    const previewDraft = useReportStore((s) => s.previewDraft);

    // Subscribe to report document (base doc)
    const {data: reportDoc, loading: docLoading, error: docError} = useReportDocumentSubscription(reportId);

    // Subscribe to versions collection (new backend structure)
    const {data: versions, loading: versionsLoading, error: versionsError} = useReportVersionsSubscription(reportId);

    // Normalize available version strings for quick membership checks
    const availableVersions = useMemo(() => {
        const derived = [];
        if (versions && versions.length > 0) {
            derived.push(
                ...versions
                    .map((v) => {
                        const formatted = formatVersionNumber(v?.version);
                        if (formatted) return formatted;
                        if (typeof v?.id === 'string' && v.id.startsWith('ver_')) return v.id.replace(/^ver_/, '');
                        if (typeof v?.id === 'string') return v.id;
                        return null;
                    })
                    .filter(Boolean)
            );
        }
        // Fallback to snapshotPath-derived version when collection data is missing
        const fromSnapshot = parseVersionFromSnapshotPath(reportDoc?.snapshotPath);
        if (fromSnapshot) derived.push(fromSnapshot);
        return Array.from(new Set(derived));
    }, [versions, reportDoc?.snapshotPath]);

    // Latest version comes from the highest available version (collection or snapshot fallback)
    const latestVersion = useMemo(() => {
        const numericVersions = availableVersions
            .map((v) => ({str: v, num: parseInt(v, 10)}))
            .filter((v) => Number.isFinite(v.num));
        if (numericVersions.length > 0) {
            const width = numericVersions.reduce((max, v) => Math.max(max, v.str?.length || 0), 7);
            const maxNum = Math.max(...numericVersions.map((v) => v.num));
            return String(maxNum).padStart(width, '0');
        }
        return parseVersionFromSnapshotPath(reportDoc?.snapshotPath);
    }, [availableVersions, reportDoc?.snapshotPath]);

    const fromEditorDraft = hasActivePreviewDraft(previewDraft, reportId);
    const resolvedVersion = resolvePreviewVersion(version, previewDraft, reportId, latestVersion);

    // Keep URL aligned with latest when preview is opened from the editor.
    useEffect(() => {
        if (useLocalState || !reportId || !latestVersion) {
            return;
        }
        if (!fromEditorDraft) {
            if (!version) {
                setSearchParams({version: latestVersion}, {replace: true});
            }
            return;
        }
        if (!version || String(version) !== String(latestVersion)) {
            setSearchParams({version: latestVersion}, {replace: true});
        }
    }, [version, latestVersion, reportId, setSearchParams, useLocalState, fromEditorDraft]);

    // If a version is requested that does not exist, fall back to the latest available
    useEffect(() => {
        if (!version || versionsLoading || fromEditorDraft) return;
        if (!availableVersions.length) return;
        const exists = availableVersions.includes(version);
        if (!exists) {
            const fallback = latestVersion || availableVersions[0];
            if (fallback && fallback !== version) {
                if (useLocalState) {
                    setLocalVersion(fallback);
                } else {
                    setSearchParams({version: fallback}, {replace: true});
                }
            }
        }
    }, [version, versionsLoading, availableVersions, latestVersion, setSearchParams, useLocalState, fromEditorDraft]);

    useEffect(() => {
        if (!useLocalState || version || !latestVersion || !reportId) {
            return;
        }
        setLocalVersion(latestVersion);
    }, [useLocalState, version, latestVersion, reportId]);

    const {
        data: versionDoc,
        loading: versionDocLoading,
        error: versionDocError
    } = useReportVersionDocSubscription(reportId, resolvedVersion);

    const {content, references, metadata: versionMetadata, loading: blobLoading, error: blobError} = useVersionBlob(
        reportId,
        resolvedVersion,
        latestVersion
    );

    // For preview header/meta we prefer versionDoc.report (new schema), fallback to base report doc.
    const headerDoc = versionDoc?.report || reportDoc;
    const meta = headerDoc?.meta;

    const documentHeader = useMemo(() => {
        if (shouldUsePreviewDraft(previewDraft, reportId, resolvedVersion, latestVersion)) {
            if (previewDraft?.metadata) {
                return previewDraft.metadata;
            }
            const ydoc = useYDocStore.getState().ydoc;
            if (ydoc) {
                const fromYdoc = readSerializableHeaderFromYDoc(ydoc);
                if (Object.keys(fromYdoc).length > 0) {
                    return fromYdoc;
                }
            }
        }
        if (versionMetadata && typeof versionMetadata === 'object' && Object.keys(versionMetadata).length > 0) {
            return mergeHeaderData(apiMetaToHeaderData(versionMetadata), apiMetaToHeaderData(meta));
        }
        return apiMetaToHeaderData(meta);
    }, [previewDraft, reportId, resolvedVersion, latestVersion, meta, versionMetadata]);

    const {authors: previewAuthors} = usePreviewAuthors({
        content,
        versionDoc,
        meta,
        documentHeader,
        reportDoc,
        reportId,
        version: resolvedVersion,
        latestVersion,
    });

    const isLoading = docLoading
        || versionsLoading
        || versionDocLoading
        || (resolvedVersion && blobLoading && !content);
    const error = docError || versionsError || versionDocError || blobError;

    return {
        version: resolvedVersion ?? version,
        latestVersion,
        versions,
        reportDoc,
        versionDoc,
        headerDoc,
        content,
        references,
        meta,
        documentHeader,
        previewAuthors,
        isLoading,
        error,
        docLoading,
        versionsLoading,
        versionDocLoading
    };
};

export default useVersionManagement;
